import { Component, Inject, OnInit, Renderer2 } from '@angular/core';
import { NavigationEnd, Router } from "@angular/router";
import { NgxPermissionsService } from "ngx-permissions";
import { combineLatest } from "rxjs";
import { filter } from 'rxjs/operators';

import { APP_CONFIG } from "./config/config";
import { AppConfig } from "./config/config.type";
import { OrganizationStoreService } from "./organizations/services/organization.store.service";
import { FunnelService } from "./shared/services/funnel.service";
import { LocaleService } from "./shared/services/locale.service";
import { NavigationService } from "./shared/services/navigation.service";
import { VersionCheckService } from "./shared/services/version-check.service";
import { Role } from "./users/models/users.entity";
import { ProfileService } from "./users/services/profile.service";

declare global {
  interface Window {
    _mtm: any;
  }
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: [ './app.component.scss' ]
})
export class AppComponent implements OnInit {

  constructor(@Inject(APP_CONFIG)
              private readonly appConfig: AppConfig,
              private readonly render: Renderer2,
              private readonly organizationStoreService: OrganizationStoreService,
              private readonly router: Router,
              private readonly profileService: ProfileService,
              private readonly permissionService: NgxPermissionsService,
              private readonly navigationService: NavigationService,
              private readonly funnelService: FunnelService,
              private readonly versionCheckService: VersionCheckService,
              private readonly localeService: LocaleService) {

  }

  ngOnInit() {
    // Initialize permissions + UI language from profile
    combineLatest([this.profileService.getObservable(), this.organizationStoreService.getObservable()])
      .subscribe(([ state, organization ]) => {
          this.permissionService.flushPermissions();
          this.permissionService.addPermission([ state.role ?? Role.Talker ]);
          if (state.role === Role.Talker && state.isPilot) {

            this.permissionService.addPermission('pilot');
            if (state?.organizationAssociations?.length > 1 || state?.organizationAssociations.some(managedOrganization => managedOrganization?.organization?.children?.length)) {
              this.permissionService.addPermission('pilot-with-multiple-organizations');
            }
          }

          if (organization) {

            this.permissionService.addPermission('organization');
          }

          this.localeService.initFromProfile(state?.lang?.name).subscribe();
        }
      );

    // Initialize version checking on route changes
    // This ensures users always run the latest deployed version
    // If a new version is detected, the app will reload automatically
    this.initializeVersionCheck();
  }

  /**
   * Initialize version checking on navigation
   *
   * ENHANCED VERSION CHECKING SYSTEM:
   * This adds route-based checks as an ADDITIONAL trigger (belt-and-suspenders approach).
   * The VersionCheckService also runs:
   * - Periodic checks every 10 minutes
   * - Checks when user returns to tab (focus/visibility change)
   *
   * Multiple triggers ensure maximum reliability for detecting new deployments.
   * Forces hard reload if new version detected (prevents cache issues).
   */
  private initializeVersionCheck(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      // Check version after navigation completes
      // If mismatch detected, service will force reload
      this.versionCheckService.checkVersion().subscribe();
    });
  }

}
